import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { FormComponent, UiComponent } from 'components';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { usePageOption } from 'hooks';
import useModal from 'hooks/useModal';
import { StickerQuery, UserGroupQuery } from 'queries';
import * as M from 'styles/mobile';
import {
  IStickerListItem,
  IUserGroupListItem,
  SearchUserGroupFilterType,
} from 'types';
import * as S from './style';

const getPhoneLast4 = (phone?: string) =>
  (phone ?? '').replace(/\D/g, '').slice(-4);

interface Props {
  sticker: IStickerListItem;
}

function GroupGrant({ sticker }: Props) {
  const [group, setGroup] = useState<IUserGroupListItem | null>(null);

  if (!group) return <GroupSelect onSelect={setGroup} />;

  return (
    <GroupMembers
      sticker={sticker}
      group={group}
      onChangeGroup={() => setGroup(null)}
    />
  );
}

function GroupSelect({
  onSelect,
}: {
  onSelect: (group: IUserGroupListItem) => void;
}) {
  const [filter, setFilter] = useState<SearchUserGroupFilterType>({});
  const { pageOption, handlePage } = usePageOption(10);
  const { data, isLoading } = UserGroupQuery.useGetAll({
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const groups: IUserGroupListItem[] = data?.data ?? [];

  const handleSearch = (d: SearchUserGroupFilterType) => {
    setFilter({ name: d.name?.trim() || undefined });
    handlePage({ ...pageOption, page: 1 });
  };

  return (
    <>
      <FormComponent.Form submit={handleSearch}>
        <FormComponent.SearchInput
          name="name"
          placeholder="그룹명으로 검색해주세요"
        />
      </FormComponent.Form>
      <div css={S.sheetList}>
        {groups.map((group) => (
          <div key={group.id} css={S.row}>
            <div css={S.info}>
              <p css={S.name}>{group.name}</p>
              <p css={S.meta}>
                회원 {group.memberCount.toLocaleString('ko')}명
              </p>
            </div>
            <UiComponent.Button
              variant="contained"
              color="primary"
              css={S.action}
              onClick={() => onSelect(group)}
            >
              선택
            </UiComponent.Button>
          </div>
        ))}
        {!isLoading && groups.length === 0 && (
          <p css={M.empty}>등록된 회원 그룹이 없습니다</p>
        )}
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </>
  );
}

function GroupMembers({
  sticker,
  group,
  onChangeGroup,
}: {
  sticker: IStickerListItem;
  group: IUserGroupListItem;
  onChangeGroup: () => void;
}) {
  const { handleConfirm } = useModal();
  const { mutateAsync: grant } = StickerQuery.useGrant();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data, isLoading } = UserGroupQuery.useGetTargets(group.id);
  const targets = data?.data ?? [];

  const handleGrant = () => {
    if (isSubmitting || targets.length === 0) return;

    handleConfirm({
      confirmContent: `'${group.name}' 그룹 ${targets.length.toLocaleString('ko')}명에게 '${sticker.name}' 스티커를 지급하시겠습니까?`,
      yesTitle: '지급',
      confirmNext: async () => {
        setIsSubmitting(true);
        try {
          const res = await grant({
            id: sticker.id,
            userIds: targets.map((t) => t.userId),
          });
          const { granted, skipped } = res.data;
          toast.success(
            `${granted}명 지급 완료${skipped ? ` (이미 보유 ${skipped}명 건너뜀)` : ''}`,
          );
        } catch {
          toast.error(DEFAULT_ERROR_MESSAGE);
        } finally {
          setIsSubmitting(false);
        }
      },
    });
  };

  return (
    <>
      <div css={S.groupHead}>
        <p>
          <strong>{group.name}</strong>
          <span>{targets.length.toLocaleString('ko')}명</span>
        </p>
        <UiComponent.Button
          variant="border"
          color="grey"
          css={S.action}
          onClick={onChangeGroup}
        >
          그룹 변경
        </UiComponent.Button>
      </div>
      <div css={S.sheetList}>
        {targets.map(({ userId, userName, userPhone }) => {
          const last4 = getPhoneLast4(userPhone);
          return (
            <div key={userId} css={S.row}>
              <p css={[S.info, S.name]}>
                {userName}
                {last4 && <span>({last4})</span>}
              </p>
            </div>
          );
        })}
        {!isLoading && targets.length === 0 && (
          <p css={M.empty}>그룹에 속한 회원이 없습니다</p>
        )}
      </div>
      <UiComponent.Button
        variant="contained"
        color="primary"
        css={S.submit}
        disabled={isSubmitting || targets.length === 0}
        onClick={handleGrant}
      >
        {`그룹 ${targets.length.toLocaleString('ko')}명에게 지급`}
      </UiComponent.Button>
    </>
  );
}

export default GroupGrant;
